import type { CheckItem } from '@/content/checks'
import type { Photo } from '@/content/sections'
import { LINKS } from '@/content/pages/links'

/**
 * The Future 2030 Services outlook page — its copy and its figures, apart from the
 * component that draws them.
 *
 * **The seventh Future 2030 outlook with a designed page of its own**, and the
 * sixteenth report overall. Asked for on 2026-08-28, in the same words as the
 * Automotive, Manufacturing, Consumer Goods, Retail, Pharmaceutical and Banking
 * ones — *"referans alarak ilgili sayfayı birebir tıpa tıp aynısını tasarla"* — as
 * a five-part full-page capture of digitopia.co's Services outlook page, plus the
 * six images that page carries: the gears-on-a-circuit-board cover art, the DMI
 * forecast chart, the three requirement photographs and the two-copy report
 * mockup. And, as on the Consumer Goods, Retail, Pharmaceutical and Banking
 * requests, the instruction that the maturity band point at the 2026 DAIMI report
 * rather than the 2024 one the live page offers.
 *
 * So the bands are the reference's, in the reference's order: hero → the forecast
 * beside Figure 1 → a dark band of three imperatives → the five predictions → the
 * pointer at the maturity report → the download close. The two shared closing bands
 * (`content/reportClosing.ts`) still sit where they sit on every other report page.
 *
 * **The shape is the Banking page's**: two lead-in lines over the predictions, a
 * dark band whose second line is a title rather than a second claim, five plain
 * statements rather than label-and-figure pairs, no closing line under the
 * trajectories, and a close headed "Download the Full Report". One thing is this
 * page's own, and it is the hero. Every sibling opens with a bold claim on its own
 * line and the reason for it underneath; this live hero is **one paragraph whose
 * closing clause is bold** — "…but *how intelligently, efficiently, and humanely
 * you deliver it*." So there is no `lede` here. `body` and `emphasis` are the two
 * halves of one sentence and the component sets them as one paragraph, because
 * breaking that clause onto a line of its own would turn a sentence into a slogan.
 *
 * **No sector slip in this reference.** The Pharmaceutical and Banking captures
 * both carry a maturity CTA reading "Explore the 2024 **Retail** Digital Maturity
 * Report" under a paragraph about their own sector — the evidence that those pages
 * were built from the Retail one. This page's CTA says Services, so there was
 * nothing to correct and `OUTLOOK_SV_MATURITY.check` does not raise it.
 *
 * **Two deliberate departures from the capture**, both the settled house call rather
 * than a reading of the reference:
 *
 *  * the h1 is the series headline every outlook page here uses ("Digital
 *    Transformation Outlook for Services") rather than the live page's "Future
 *    2030: Services Industry Outlook" — the eyebrow above it already says Future
 *    2030, and the seven outlook pages read as one series this way.
 *  * the maturity band carries the 2026 Services Industry Focus cover, which the
 *    live band does not. `OUTLOOK_SV_MATURITY.check` is where that is asked about,
 *    because it is the one place this page shows something the reference has not.
 *
 * **Where the words come from.** Everything here is transcribed from the five
 * captures, which are full-page renders read down to fit — the *shape* of the page
 * is certain and the *wording* is a careful reading of body copy set a handful of
 * pixels tall. Every band that carries prose therefore carries a `check`, the same
 * way `content/outlookBanking.ts` does, and `outlookServicesChecks()` feeds them
 * into the same manifest so a production build cannot ship them unread.
 *
 * **What the live page has that this page cannot.** Its Download buttons and its
 * closing band open an embedded HubSpot form — first and last name, business email,
 * company, job title, phone, country, industry, a marketing opt-in and the privacy
 * statement. These documents are prerendered with no client bundle at all
 * (`src/prerender.tsx`), so a form script would never run, and the other thirty
 * reports on this site hand the reader the PDF from our own bucket instead. Every
 * Download button here is that PDF, and `OUTLOOK_SV_CLOSE.check` is what keeps the
 * gating question open.
 */

/**
 * The report this page belongs to — the branch `pages/Reports.tsx` takes on it.
 *
 * The short slug digitopia.co uses, and the one the request named. It was already in
 * `REPORTS` before this page existed, so the page is built under it as it stands
 * rather than moved to a `…-outlook-2030` one.
 */
export const OUTLOOK_SV_SLUG = 'services-industry-outlook'

/**
 * The Services Industry Focus cut of the Digital & AI Maturity Report 2026 — where
 * this page's maturity band sends the reader.
 *
 * Written out rather than imported: that report is one of the twenty drawn by the
 * shared template in `pages/Reports.tsx`, so it has no content module of its own to
 * export a slug from. It is the literal passed to `focus()` in
 * `content/reports.ts`, and the two have to stay in step. (The Banking page can
 * import its equivalent because the Banking cut has a designed page; the Services
 * cut does not, yet.)
 */
export const DAIMI_SV_SLUG = 'digital-and-ai-maturity-report-2026-services-industry'

/**
 * The hero.
 *
 * **The one band that is not shaped like its siblings'.** There is no bold claim on
 * a line of its own: the live hero is a single paragraph, and only its last clause
 * is set bold. `body` is everything before that clause and `emphasis` is the clause
 * itself, with the full stop supplied by the component — so the two read as one
 * sentence however they wrap.
 *
 * The button says "Download the Report" rather than the "Download Report" every
 * other band uses, which is also the live page's wording.
 */
export const OUTLOOK_SV_INTRO = {
  body: 'Across hospitality, healthcare, education, and professional services, a shift is underway. Services are no longer delivered. They’re predicted, personalized, and platform-enabled. By 2030, success won’t hinge on what you offer, but on ',
  emphasis: 'how intelligently, efficiently, and humanely you deliver it',
  downloadLabel: 'Download the Report',
  /**
   * The cover art supplied with this request — the three gears standing on a glowing
   * circuit board, which is the artwork behind the report cover with none of its
   * lettering on it. This is where the live hero puts it too, at the same size and on
   * the same side.
   */
  photo: {
    src: '/photo/future-2030-services-digital-transformation.webp',
    alt: 'Three brass gears standing on a glowing blue circuit board',
  } satisfies Photo,
  check:
    'The hero paragraph is transcribed from the reference capture rather than supplied as text, and so is the decision about where its bold begins — the live page bolds “how intelligently, efficiently, and humanely you deliver it” and nothing before it. Read both back against digitopia.co before production. The image, its side and its size are the live page’s.',
}

/**
 * The forecast band: Figure 1 on the left, the three trajectories on the right.
 *
 * The three are one block on the live page in which each line opens with a bolded
 * label — "Leaders", "Average performers", "Laggards". Split into `label` and `body`
 * here so the label can be set rather than emphasised inside a sentence, which is the
 * call every designed report page on this site has made.
 *
 * No closing line: like the Banking and Pharmaceutical bands and unlike the Retail
 * one, the live band goes straight from the third trajectory to the Download button.
 */
export const OUTLOOK_SV_FIGURE = {
  heading: 'Services Digital Maturity Forecast to 2030',
  intro: 'Digitopia’s DMI data shows three clear paths ahead:',
  points: [
    {
      label: 'Leaders',
      body: 'Are approaching fully intelligent service delivery, combining AI, automation, and personalization at scale.',
    },
    {
      label: 'Average performers',
      body: 'Risk becoming interchangeable unless they shift from digitization to true reinvention.',
    },
    {
      label: 'Laggards',
      body: 'Face an existential threat unless they transform, urgently and boldly.',
    },
  ],
  /**
   * Figure 1, the file supplied with this request: five years of actuals (2020–24)
   * and five of forecast (2025–29), the leader, average and laggard trajectories
   * each fanning out into an optimistic, moderate and pessimistic band. It is
   * 1200×784 on a white ground, so it gets a white card of its own and scales to it
   * whole — ten year columns with three labelled values each do get small on a
   * phone, but a figure the reader has to drag sideways reads as broken.
   */
  photo: {
    src: '/photo/future-2030-services-dmi-forecast.webp',
    alt: 'Services DMI evolution 2020 to 2029: actual leader, average and laggard scores to 2024, then optimistic, moderate and pessimistic forecast bands to 2029',
  } satisfies Photo,
  caption: [
    'Figure 1: Services Industry DMI Evolution (2020–2029) with Scenario-Based Forecasts',
    'Leader, Average, and Laggard Trajectories under Optimistic, Moderate, and Pessimistic Projections',
  ],
  check:
    'Figure 1 is the file you supplied and is shown whole. The three trajectories beside it are transcribed from the reference and want a reading — unlike the Banking page’s, none of them quotes a number, so there is nothing here to reconcile against the chart, only wording. Each label’s sentence starts on its own line here, so the live page’s lower-case openings (“are approaching…”) are capitalised. One naming question, the same one the Banking page raised: this page calls the index DMI while every other page on this site calls it DAIMI, the Digital & AI Maturity Index. The chart itself is lettered DMI, so both were left as they are — say which name this page should use.',
}

/**
 * The dark imperatives band: two headline lines, then three cards.
 *
 * The live band's second line is not a sentence but a title — "Three Imperatives for
 * Future-Ready Service Providers" — so it is set lighter and larger under the
 * headline rather than as a second claim, which is what the component already does
 * with it.
 */
export const OUTLOOK_SV_REQUIREMENTS = {
  heading: 'Services Digital Revolution Has Arrived',
  subheading: 'Three Imperatives for Future-Ready Service Providers',
  /** The eyebrow above each card's title, numbered in the order the live band has. */
  eyebrow: 'How to Get There',
  items: [
    {
      title: 'Hyper-Automate Everything',
      body: 'Deliver faster, smarter, and leaner by eliminating manual bottlenecks and streamlining customer journeys.',
      linkLabel: 'Digital Solutions',
      linkHref: LINKS.solutions,
      photo: {
        src: '/photo/services-hyper-automation.webp',
        alt: 'A hand reaching down to a glowing blue touch interface',
      } satisfies Photo,
    },
    {
      title: 'Adopt an AI-First Mindset',
      body: 'From GenAI-generated scripts to predictive support, embed intelligence into every touchpoint.',
      linkLabel: 'Artificial Intelligence',
      linkHref: LINKS.enterpriseAi,
      photo: {
        src: '/photo/services-ai-first-mindset.webp',
        alt: 'Two people shaking hands over a city street rendered as a lit data landscape',
      } satisfies Photo,
    },
    {
      title: 'Empower Your People',
      body: 'Equip your workforce with the digital fluency, AI literacy, and agile mindset.',
      linkLabel: 'Digital Upskilling',
      linkHref: LINKS.transformation,
      photo: {
        src: '/photo/services-empowered-people.webp',
        alt: 'A smiling service agent wearing a headset at her desk',
      } satisfies Photo,
    },
  ],
  check:
    'Two things. The two headline lines and the three card texts are transcribed from the reference capture. And the third card’s link is the one destination that had to change: the live page sends “Digital Upskilling” to a solution page digitopia.co has and this site does not — it came across in the import as a rebuild source rather than as a page, so linking to it would be a dead link. It points at Transformation Management until that page is built. The other two are the live destinations. This is the same substitution the Automotive, Manufacturing, Consumer Goods, Retail, Pharmaceutical and Banking outlook pages make, and one answer settles all seven.',
}

/**
 * The predictions band, with the report mockup beside it.
 *
 * Five plain statements rather than the Automotive page's label-and-figure pairs,
 * because that is what the live band prints. Two lead-in lines rather than one: the
 * live band states what the next era rewards before it introduces the list.
 */
export const OUTLOOK_SV_PREDICTIONS = {
  heading: 'Five Predictions for the 2030 Services Industry',
  intro: [
    'The next era of services will reward intelligence, agility, and trust.',
    'As digital expectations surge and customer journeys fragment, service providers must radically rethink how they operate, scale, and engage. These five shifts will reshape the entire industry by 2030, demanding bold moves from today’s leaders.',
  ],
  items: [
    'Platforms over pipelines: Scalable, API-driven models replace traditional one-to-one delivery',
    'Hyper-automation becomes the operational standard',
    'The gig economy reshapes service teams',
    'Service intelligence defines competitive edge',
    'Digital trust and cyber-resilience become must-haves',
  ],
  /** The mockup supplied with this request — the Future 2030 cover, not a maturity cut's. */
  photo: {
    src: '/photo/future-2030-outlook-services-mockup.webp',
    alt: 'Two copies of Future 2030: Digital Transformation Outlook for Services',
  } satisfies Photo,
  check:
    'The heading, both lead-in lines and all five predictions are transcribed from the reference capture. None of the five carries a figure, so there is nothing here to reconcile against Figure 1 — only wording. The mockup beside them is the file you supplied; the reference capture shows that half of the band empty, so say if the live page really carries no image there and it should come out. This is the same call the Retail and Banking pages made.',
}

/**
 * The band pointing at the maturity report.
 *
 * **The one band whose content is deliberately not the live page's, and the only
 * change in this request that was asked for in words rather than shown in a
 * picture.** digitopia.co sends the reader to a *2024* report; the instruction with
 * the captures was *"en aşağıda önceden konuştuğumuz gibi 2024'e değil 2026 DAIMI
 * report'a yönlendirelim"*. So the CTA, the cover and the sentence all name the
 * Services Industry Focus cut of the Digital & AI Maturity Report 2026, which is the
 * report this site actually publishes.
 *
 * That is settled, so it is not what `check` asks about: the same swap was left open
 * on the Automotive page and has now been answered seven times.
 */
export const OUTLOOK_SV_MATURITY = {
  heading: 'How Does Your Digital Maturity Compare?',
  body: 'The Services Industry Focus cut of the Digital & AI Maturity Report 2026 reveals where companies stand today, and where they need to go. Compare your performance with leaders, average performers, and laggards. Are you leading the shift or reacting to it? Use our data to compare your performance, assess your risks, and find the fastest route to transformation.',
  ctaLabel: 'Explore the Services Industry Focus report',
  ctaHref: `/en/impact-labs/reports/${DAIMI_SV_SLUG}`,
  /** That report's cover, which this site already holds. */
  photo: {
    src: '/photo/daimi-report-2026-services-cover.webp',
    alt: 'Digital & AI Maturity Report 2026, Services Focus',
  } satisfies Photo,
  check:
    'The destination here is decided, not transcribed: this band points at the 2026 report rather than the “2024 Services Digital Maturity Report” the live page offers, which is what you asked for — so the CTA label and the sentence naming the report are the 2026 cut’s. Unlike the Pharmaceutical and Banking references, this one names the right sector, so nothing had to be corrected. Three things left to decide. The rest of the paragraph is the live one, so it wants a reading. The heading is the one every outlook page here uses; the live one runs two headings together — “How Does Your Digital Maturity Compare to Industry Leaders? Benchmark Your Digital Maturity” — which reads as a template left half-filled, so say if the longer form is really wanted. And the cover beside it is not on the live page at all: it is here because the other six Future 2030 outlook pages show the maturity cut they point at, and it should come out if this band is meant to stay text-only.',
}

/**
 * The closing band, which is where the live page's gated form sits.
 *
 * Its heading and its sentence are the live band's own — the Retail page closes with
 * an invitation ("Lead the Change") because its live close is headed that way; this
 * one is headed "Download the Full Report", so it is, the same as the Pharmaceutical
 * and Banking pages'.
 */
export const OUTLOOK_SV_CLOSE = {
  heading: 'Download the Full Report',
  body: 'Get five-year DMI projections, executive priorities, and actionable insights to shape your 2030 strategy.',
  expertLabel: 'Talk to an expert',
  // `#contact` rather than the Contact Us page: the form sits at the foot of
  // this very page, so the button scrolls instead of navigating away.
  expertHref: '#contact',
  /**
   * Resolved 2026-08-30. This band asked whether the report should sit behind
   * the form the live page gates it with, because every Download button here
   * linked straight to the bucket. It now does: the buttons carry
   * `data-gated-download` (`lib/gatedDownload.ts`), so the reader is asked for
   * an email at the foot of this page and `website-form-lead` sends the PDF to
   * it. There is no direct link left on the page to gate.
   */
  check: undefined,
}

/**
 * The page's open questions, in the shape `build-manifest.json` records copy checks
 * in — the same mechanism `outlookBankingChecks()` uses, and appended to the same
 * list in `reportChecks()`.
 *
 * `'content'` rather than `'asset'`: nothing here is a missing file. This page
 * arrived with all six of its images. What is left is transcription nobody has read
 * against the original, one destination this site cannot offer, one image this page
 * shows that the reference does not, and the gating decision.
 */
export function outlookServicesChecks(): CheckItem[] {
  const sections: { section: string; check?: string }[] = [
    { section: 'hero', check: OUTLOOK_SV_INTRO.check },
    { section: 'outlook', check: OUTLOOK_SV_FIGURE.check },
    { section: 'requirements', check: OUTLOOK_SV_REQUIREMENTS.check },
    { section: 'predictions', check: OUTLOOK_SV_PREDICTIONS.check },
    { section: 'maturity', check: OUTLOOK_SV_MATURITY.check },
    { section: 'download', check: OUTLOOK_SV_CLOSE.check },
  ]
  return sections
    .filter((s): s is { section: string; check: string } => Boolean(s.check))
    .map((s) => ({
      locale: 'en',
      page: `impact-labs/reports/${OUTLOOK_SV_SLUG}`,
      section: s.section,
      text: s.check,
      severity: 'content',
    }))
}
